/**
 * How-to guides for working in Project Hub, shown on Settings › Help.
 *
 * Each guide is a collapsed <details>, so the tab reads as a list of titles
 * until you open the one you need. Written against the app as it is: when a
 * screen or label a guide names changes, update the guide with it.
 */

import { Guide, listStyle, subList } from "./guideParts";
import { SystemGuides } from "./SystemGuides";

export function HelpGuides() {
  return (
    <div style={{ marginTop: 6 }}>
      <Guide title="Opening a new project">
        <ol style={listStyle}>
          <li>
            <b>Create it in Todoist first.</b> Add the project, its sections and its first tasks there. Todoist is
            the source of truth: Project Hub mirrors it and never writes back. The name, dates, colour and whether
            it's archived all come from Todoist. The <b>+ Project in Todoist</b> button on Projects opens it for you.
          </li>
          <li>
            <b>Let it sync.</b> Project Hub syncs when you come back to its window (at most every 5 minutes). To pull it
            in right away, go to Settings › Connections › Todoist and click <b>Sync now</b>.
          </li>
          <li>
            <b>Make sure the client exists.</b> Under Clients, click <b>New client</b> if they aren't there yet. Set
            their <b>Default rate</b> (used for hourly work when the project has no rate of its own) and any
            reimbursement notes for expenses.
          </li>
          <li>
            <b>Open the project and click Edit.</b> Then fill in:
            <ul style={subList}>
              <li><b>Client</b>: choose it from the list. A name marked "not in Clients" gets no rate, so time logged to it shows $0.</li>
              <li><b>Category</b> (Work or Personal), and a <b>Folder</b> or <b>parent project</b> if it belongs under one.</li>
            </ul>
          </li>
          <li>
            <b>Set the billing.</b> This decides how time logged to the project is counted:
            <ul style={subList}>
              <li><b>Hourly</b>: enter the hourly rate, or leave it blank to use the client's default rate.</li>
              <li><b>Fixed fee</b>: enter the <b>budgeted hours</b> and the <b>fee amount</b>. Time logs as Project work and counts against the hours.</li>
              <li><b>Monthly retainer</b>: enter <b>hours per month</b> and the <b>monthly fee</b>. The allowance resets each month.</li>
              <li><b>Internal</b>: your own work, never charged.</li>
            </ul>
          </li>
          <li>
            <b>Fill in the paperwork fields.</b> Contract (MSA or SOW reference or link), Scope and Description. The fee
            shown on the project comes from the fee amount in the billing step.
          </li>
          <li>
            <b>Check that it shows up.</b> Fixed-fee and retainer projects appear under Reports › Projects with a
            burn-down. If you'll have expenses, start a report in Expenses (or in Reimburse on your phone) and pick
            this client and project.
          </li>
        </ol>
      </Guide>

      <Guide title="Closing a project">
        <ol style={listStyle}>
          <li>
            <b>Finish or clear the open tasks.</b> Mark them done or delete them in Todoist, so the next sync doesn't
            bring them back.
          </li>
          <li>
            <b>Settle the time.</b>
            <ul style={subList}>
              <li><b>Hourly</b>: on Reports › Time, click <b>Invoice attachment…</b>, bill the unbilled time, then click <b>Mark invoiced</b>.</li>
              <li><b>Fixed fee or retainer</b>: nothing to invoice. The fee covers the time, so it's never waiting on an invoice. Check the hours used against the budget (project header or Reports › Projects) to see how the job came out.</li>
            </ul>
          </li>
          <li>
            <b>Send the final invoice for the fee.</b> Project Hub records the fee amount but doesn't bill it. Send the
            last payment through your usual invoicing.
          </li>
          <li>
            <b>Close out the expenses.</b> Open the project's report in Expenses, build and send the package, then click{" "}
            <b>Mark invoiced &amp; archive</b>.
          </li>
          <li>
            <b>Archive it in Todoist once nothing else is coming in.</b> The next sync marks it Archived here and keeps
            everything (client, billing, tasks, time). It leaves the sidebar and the timer, quick-add and time-logging
            lists, and moves to Archived on Projects. Leave it active until the last hour is logged and the final
            invoice is paid.
          </li>
          <li>
            <b>A late hour after archiving?</b> Unarchive it in Todoist, log the time once it's synced back as Active,
            then archive it again.
          </li>
          <li>
            <b>Don't delete it in Todoist</b> unless you want it gone here too. A deleted project is removed on the next
            sync, with its client, billing, fee, scope and tasks. Logged time is kept but flagged as orphaned.
          </li>
        </ol>
      </Guide>

      <Guide title="Planning your week (work blocks)">
        <p style={{ margin: "8px 0 0" }}>
          Planner › <b>Week ahead</b> puts blocks of focus time on your Google Calendar, around your meetings.
        </p>
        <ol style={listStyle}>
          <li>
            <b>Standing blocks are the base.</b> Unfold <b>Standing blocks</b> at the bottom of Week ahead. Each is time
            you want every week: a project's regular time ("PMP, 1 h, 3 a week") or routine work that isn't on a task
            list. Pick its purpose:
            <ul style={subList}>
              <li><b>Project work</b> counts against the hours estimated on that project's tasks.</li>
              <li><b>Routine</b> is protected time. It uses up the week but covers no tasks.</li>
            </ul>
            Optional rules: a preferred day or time, a window ("done by 2 PM"), and which days it may go on. Blocks
            higher in the list get first pick of the week.
          </li>
          <li>
            <b>Estimate the week's tasks.</b> Tasks scheduled or due that week, plus anything late, are listed by
            project. Type an estimate on each row as h:mm: <code>1:10</code> is an hour and ten minutes, and a plain
            number is minutes (<code>10</code> is ten minutes, <code>120</code> two hours). It's saved on the task, so a task that slips arrives next week already estimated. Click ✕
            to leave a task out of this week, or <b>+ Add a task</b> to bring one in.
          </li>
          <li>
            <b>Check each project's extra blocks.</b> Each project shows its estimate minus what its standing blocks
            already cover, and proposes that many extra blocks. Change the number with − and +. Extra blocks are for
            that week only, and go in before the project's last deadline that week.
          </li>
          <li>
            <b>Preview</b> reads your calendars and shows where everything would go, plus your free time for the
            week. Nothing is written.
          </li>
          <li>
            <b>Send to Google</b> writes the blocks. Or let it happen on its own: the first time you open Project Hub
            after your planning time (Sunday 5 PM to start with), it plans the coming week once. Change that time
            under <b>Hours &amp; defaults</b>.
          </li>
        </ol>
        <p style={{ margin: "8px 0 0" }}>
          <b>Good to know.</b> Re-sending replaces only blocks that haven't started; past ones stay. A block you drag to
          another time in Google stays put, and counts toward its weekly number. Copies of a block go on different
          days where they can. If a block says <b>No open slot</b>, its window or days were too tight for that week:
          widen them or free some time. Connection status is under Settings › Connections.
        </p>
      </Guide>


      <SystemGuides />
    </div>
  );
}
